import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Refresh,
  Clear,
  Save,
  Cancel,
  FilterList
} from '@mui/icons-material';

const Periodes = () => {
  const [data, setData] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [filiereNiveaux, setFiliereNiveaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [filtreAnnee, setFiltreAnnee] = useState('');
  const [filtreFiliere, setFiltreFiliere] = useState('');
  const [filtreNiveau, setFiltreNiveau] = useState('');
  
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    code: '',
    ordre: 0,
    niveau_id: '',
    filiere_id: '',
    annee_universitaire_id: '',
    dateDebut: '',
    dateFin: '',
    estActive: true
  });

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [periodesRes, niveauxRes, anneesRes, filieresRes, fnRes] = await Promise.all([
        axios.get('/api/periodes', { headers: getAuthHeaders() }),
        axios.get('/api/niveaux', { headers: getAuthHeaders() }),
        axios.get('/api/annees', { headers: getAuthHeaders() }),
        axios.get('/api/filieres', { headers: getAuthHeaders() }),
        axios.get('/api/filiere-niveaux', { headers: getAuthHeaders() })
      ]);
      setData(periodesRes.data || []);
      setNiveaux(niveauxRes.data || []);
      setAnnees(anneesRes.data || []);
      setFilieres(filieresRes.data || []);
      setFiliereNiveaux(fnRes.data || []);
      setError('');
    } catch (err) {
      console.error('Erreur:', err);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  const getNiveau = (id) => niveaux.find(n => n.niveau_id === id);
  const getAnnee = (id) => annees.find(a => a._id === id || a.annee_id === id);
  const getFiliere = (id) => filieres.find(f => f.filiere_id === id);

  // Helper to get all valid ID formats for a given Annee value
  const getAnneeIds = (value) => {
    if (!value) return [];
    const annee = annees.find(a => (a.annee_id || a._id) === value);
    return annee ? [value, String(annee._id), annee.annee_id].filter(Boolean) : [value];
  };

  const anneeIdsForFilter = getAnneeIds(filtreAnnee);

  // Cascade filter bar
  const filteredFilieres = filtreAnnee ? filieres.filter(f => anneeIdsForFilter.includes(f.annee_id)) : filieres;
  const filteredNiveaux = filtreFiliere
    ? niveaux.filter(n => filiereNiveaux.some(fn => fn.niveau_id === n.niveau_id && fn.filiere_id === filtreFiliere))
    : (filtreAnnee
        ? niveaux.filter(n =>
            filiereNiveaux.some(fn =>
              fn.niveau_id === n.niveau_id &&
              filieres.some(f => f.filiere_id === fn.filiere_id && anneeIdsForFilter.includes(f.annee_id))
            )
          )
        : niveaux);

  // Cascade form - Annee → Filiere (narrows Niveau options)
  const anneeIdsForForm = getAnneeIds(formData.annee_universitaire_id);
  const formFilteredFilieres = formData.annee_universitaire_id
    ? filieres.filter(f => anneeIdsForForm.includes(f.annee_id))
    : [];
  const formFilteredNiveaux = formData.filiere_id
    ? niveaux.filter(n => filiereNiveaux.some(fn => fn.niveau_id === n.niveau_id && fn.filiere_id === formData.filiere_id))
    : (formData.annee_universitaire_id
        ? niveaux.filter(n =>
            filiereNiveaux.some(fn =>
              fn.niveau_id === n.niveau_id &&
              filieres.some(f => f.filiere_id === fn.filiere_id && anneeIdsForForm.includes(f.annee_id))
            )
          )
        : niveaux);

  const filteredData = data.filter(item => {
    const search = searchTerm.toLowerCase();
    const niveau = getNiveau(item.niveau_id);
    const annee = getAnnee(item.annee_universitaire_id);

    if (filtreAnnee) {
      const directMatch = anneeIdsForFilter.includes(item.annee_universitaire_id);
      if (directMatch) {
      } else if (niveau) {
        const hasAnnee = filiereNiveaux.some(fn =>
          fn.niveau_id === niveau.niveau_id &&
          filieres.some(f => anneeIdsForFilter.includes(f.annee_id))
        );
        if (!hasAnnee) return false;
      } else {
        return false;
      }
    }
    if (filtreFiliere) {
      if (!niveau) return false;
      if (!filiereNiveaux.some(fn => fn.niveau_id === niveau.niveau_id && fn.filiere_id === filtreFiliere)) return false;
    }
    if (filtreNiveau && item.niveau_id !== filtreNiveau) return false;

    return (item.nom || '').toLowerCase().includes(search) ||
           (item.code || '').toLowerCase().includes(search) ||
           (niveau?.intitule || '').toLowerCase().includes(search) ||
           (annee?.intitule || '').toLowerCase().includes(search);
  });

  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    if (item) {
      setSelectedItem(item);
      setFormData({
        nom: item.nom || '',
        code: item.code || '',
        ordre: item.ordre || 0,
        niveau_id: item.niveau_id || '',
        filiere_id: '',
        annee_universitaire_id: item.annee_universitaire_id || '',
        dateDebut: item.dateDebut?.split('T')[0] || '',
        dateFin: item.dateFin?.split('T')[0] || '',
        estActive: item.estActive !== undefined ? item.estActive : true
      });
    } else {
      resetForm();
      const active = annees.find(a => a.estActive);
      if (active) setFormData(prev => ({ ...prev, annee_universitaire_id: active.annee_id || active._id }));
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      code: '',
      ordre: 0,
      niveau_id: '',
      filiere_id: '',
      annee_universitaire_id: '',
      dateDebut: '',
      dateFin: '',
      estActive: true
    });
  };

  const handleSubmit = async () => {
    try {
      if (modalMode === 'add') {
        await axios.post('/api/periodes', formData, { headers: getAuthHeaders() });
        setSuccessMessage('Période ajoutée avec succès');
      } else {
        await axios.put(`/api/periodes/${selectedItem._id}`, formData, { headers: getAuthHeaders() });
        setSuccessMessage('Période modifiée avec succès');
      }
      await loadData();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'opération');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer définitivement ${item.nom} ?`)) return;
    try {
      await axios.delete(`/api/periodes/${item._id}`, { headers: getAuthHeaders() });
      setSuccessMessage('Période supprimée avec succès');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError('')}
        message={error}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1f2937' }}>
            📋 Gestion des Périodes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredData.length} période(s) trouvée(s)
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
            Actualiser
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal('add')}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            Nouvelle Période
          </Button>
        </Stack>
      </Box>

      {/* Filtres en cascade */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList color="action" />
            <Typography variant="body2" fontWeight="500" color="text.secondary">
              Filtres :
            </Typography>
          </Box>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Année</InputLabel>
            <Select
              value={filtreAnnee}
              label="Année"
              onChange={(e) => { setFiltreAnnee(e.target.value); setFiltreFiliere(''); setFiltreNiveau(''); setPage(0); }}
            >
              <MenuItem value="">Toutes les années</MenuItem>
              {annees.map((a) => (
                <MenuItem key={a._id || a.annee_id} value={a.annee_id || a._id}>
                  {a.intitule} {a.estActive ? '⭐' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }} size="small" disabled={!filtreAnnee}>
            <InputLabel>Filière</InputLabel>
            <Select
              value={filtreFiliere}
              label="Filière"
              onChange={(e) => { setFiltreFiliere(e.target.value); setFiltreNiveau(''); setPage(0); }}
            >
              <MenuItem value="">Toutes les filières</MenuItem>
              {filteredFilieres.map((f) => (
                <MenuItem key={f.filiere_id} value={f.filiere_id}>{f.code_filiere || f.libelle} - {f.intitule}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }} size="small" disabled={!filtreAnnee}>
            <InputLabel>Niveau</InputLabel>
            <Select
              value={filtreNiveau}
              label="Niveau"
              onChange={(e) => { setFiltreNiveau(e.target.value); setPage(0); }}
            >
              <MenuItem value="">Tous les niveaux</MenuItem>
              {filteredNiveaux.map((n) => (
                <MenuItem key={n.niveau_id} value={n.niveau_id}>{n.intitule}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {(filtreAnnee || filtreFiliere || filtreNiveau) && (
            <Button size="small" variant="outlined" onClick={() => { setFiltreAnnee(''); setFiltreFiliere(''); setFiltreNiveau(''); }} startIcon={<Clear />}>
              Effacer
            </Button>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par nom, code, niveau ou année..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ background: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nom</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Ordre</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Niveau</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Année</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Dates</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => {
              const niveau = getNiveau(item.niveau_id);
              const annee = getAnnee(item.annee_universitaire_id);
              return (
                <TableRow key={item._id} hover>
                  <TableCell sx={{ fontWeight: 600, color: '#667eea', fontFamily: 'monospace' }}>{item.code}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{item.nom}</TableCell>
                  <TableCell>{item.ordre}</TableCell>
                  <TableCell>
                    <Chip label={niveau?.intitule || '-'} size="small" sx={{ bgcolor: '#f3e8ff', color: '#7c3aed' }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={annee?.intitule || '-'} size="small" sx={{ bgcolor: '#e0e7ff', color: '#4338ca' }} />
                  </TableCell>
                  <TableCell>
                    {item.dateDebut ? new Date(item.dateDebut).toLocaleDateString('fr-FR') : '-'} 
                    {' → '} 
                    {item.dateFin ? new Date(item.dateFin).toLocaleDateString('fr-FR') : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.estActive ? '✅ Active' : '❌ Inactive'}
                      size="small"
                      sx={{ bgcolor: item.estActive ? '#dcfce7' : '#fee2e2', color: item.estActive ? '#166534' : '#991b1b' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Modifier">
                        <IconButton size="small" color="warning" onClick={() => handleOpenModal('edit', item)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredData.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">Aucune période trouvée</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredData.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        labelRowsPerPage="Lignes par page"
        sx={{ mt: 2 }}
      />

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {modalMode === 'add' ? '➕ Ajouter une période' : '✏️ Modifier la période'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Code *"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nom *"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Ordre"
                  type="number"
                  value={formData.ordre}
                  onChange={(e) => setFormData({ ...formData, ordre: parseInt(e.target.value) || 0 })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Année Universitaire *</InputLabel>
                  <Select
                    value={formData.annee_universitaire_id}
                    onChange={(e) => setFormData({ ...formData, annee_universitaire_id: e.target.value, filiere_id: '', niveau_id: '' })}
                    label="Année Universitaire *"
                  >
                    <MenuItem value="">Sélectionner</MenuItem>
                    {annees.map((a) => (
                      <MenuItem key={a._id || a.annee_id} value={a.annee_id || a._id}>
                        {a.intitule} {a.estActive ? '⭐' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" disabled={!formData.annee_universitaire_id}>
                  <InputLabel>Filière</InputLabel>
                  <Select
                    value={formData.filiere_id || ''}
                    onChange={(e) => setFormData({ ...formData, filiere_id: e.target.value, niveau_id: '' })}
                    label="Filière"
                  >
                    <MenuItem value="">Toutes les filières</MenuItem>
                    {formFilteredFilieres.map((f) => (
                      <MenuItem key={f.filiere_id} value={f.filiere_id}>{f.code_filiere || f.libelle} - {f.intitule}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" disabled={!formData.annee_universitaire_id}>
                  <InputLabel>Niveau *</InputLabel>
                  <Select
                    value={formData.niveau_id}
                    onChange={(e) => setFormData({ ...formData, niveau_id: e.target.value })}
                    label="Niveau *"
                  >
                    <MenuItem value="">Sélectionner</MenuItem>
                    {formFilteredNiveaux.map((n) => (
                      <MenuItem key={n.niveau_id} value={n.niveau_id}>
                        {n.intitule}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date Début"
                  type="date"
                  value={formData.dateDebut}
                  onChange={(e) => setFormData({ ...formData, dateDebut: e.target.value })}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date Fin"
                  type="date"
                  value={formData.dateFin}
                  onChange={(e) => setFormData({ ...formData, dateFin: e.target.value })}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.estActive ? 'actif' : 'inactif'}
                    onChange={(e) => setFormData({ ...formData, estActive: e.target.value === 'actif' })}
                    label="Statut"
                  >
                    <MenuItem value="actif">Active</MenuItem>
                    <MenuItem value="inactif">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} startIcon={<Cancel />}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" startIcon={<Save />} sx={{ bgcolor: '#667eea' }}>
            {modalMode === 'add' ? 'Ajouter' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Periodes;


